import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/useAuth'
import { useSubscription } from '../context/useSubscription'
import { useWorkspace } from '../context/useWorkspace'
import { isNoSignInPreviewActive } from '../debug'
import {
  checkIfSaved as checkSavedList,
  copyChecklistTemplateToVisionboard,
  deleteLocalTemplate,
  importTemplate,
  mergeTemplateRegistryEntries,
  subscribeSavedTemplates,
  subscribeTemplateRegistry,
  subscribeWorkspaceTemplateClones,
} from '../services/exploreRegistryService'
import type { SavedTemplateRecord, TemplateCategory, TemplateRegistryEntry, WorkspaceTemplateClone } from '../types/templates'
import { BUNDLED_EXPLORE_TEMPLATES } from '../data/explore_templates'

const debugTemplateClone = (
  category: TemplateCategory,
  templateId: string,
  title: string,
  summary: string,
  payload: Record<string, unknown>,
): WorkspaceTemplateClone => ({
  id: templateId,
  workspaceId: 'debug-workspace',
  templateId,
  sourceTemplateId: templateId,
  category,
  title,
  registryVersion: 1,
  sourceRegistryVersion: 1,
  importedBy: 'debug-user',
  importedByName: 'Debug User',
  importedAt: null,
  updatedAt: null,
  sourcePreviewData: { title, summary, metrics: [], sections: [], payload },
  previewData: { title, summary, metrics: [], sections: [], payload },
  isModified: false,
  modifiedAt: null,
})

const DEBUG_TEMPLATE_CLONES: WorkspaceTemplateClone[] = [
  debugTemplateClone('Automations', 'debug-automation-deep-work-reward-loop', 'Deep Work Reward Loop', 'Debug template: multi-trigger AutomationGraph awarding XP for focus and task wins.', {
    automationVersion: 2,
    graph: {
      name: 'Deep Work Reward Loop',
      version: 2,
      status: 'PAUSED',
      triggers: [
        { type: 'FOCUS_BLOCK_COMPLETED', config: { minMinutes: 15 } },
        { type: 'NODE_COMPLETED', config: { scope: 'WORKSPACE', nodeKind: 'TASK' } },
      ],
      then: [{ id: 'debug-xp', type: 'AWARD_XP', config: { amount: 25 } }],
    },
  }),
  debugTemplateClone('Designs', 'debug-design-product-flow', 'Product Flow Diagram', 'Debug template that creates lanes, stages, decisions, and connectors.', {
    designMode: 'BOUND',
    lanes: ['Customer', 'Product', 'Operations'],
    nodes: [
      { id: 'discover', type: 'stage', x: 120, y: 160, label: 'Discover problem' },
      { id: 'evaluate', type: 'stage', x: 400, y: 160, label: 'Evaluate options' },
      { id: 'commit', type: 'decision', x: 700, y: 160, label: 'Commit?' },
      { id: 'activate', type: 'stage', x: 1000, y: 160, label: 'Activate' },
    ],
    connectors: [
      { from: 'discover', to: 'evaluate' },
      { from: 'evaluate', to: 'commit' },
      { from: 'commit', to: 'activate' },
    ],
  }),
]

export const useTemplateEngine = (category: TemplateCategory | null = null) => {
  const { user, currentUser } = useAuth()
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const activeWorkspace = useMemo(() => workspaces.find((workspace) => workspace.id === activeWorkspaceId), [activeWorkspaceId, workspaces])
  const { plan } = useSubscription()
  const actor = user ?? currentUser
  const isPreview = isNoSignInPreviewActive()

  const [subscribedRegistry, setSubscribedRegistry] = useState<{
    category: TemplateCategory | null
    templates: TemplateRegistryEntry[]
    error: string | null
  } | null>(null)

  useEffect(() => {
    if (isPreview) return
    const stop = subscribeTemplateRegistry(
      category,
      (templates) => setSubscribedRegistry({ category, templates, error: null }),
      (nextError) => setSubscribedRegistry({ category, templates: [], error: nextError.message }),
    )
    return () => stop()
  }, [category, isPreview])

  const [subscribedSaved, setSubscribedSaved] = useState<{
    workspaceId: string | null
    saved: SavedTemplateRecord[]
    clones: WorkspaceTemplateClone[]
    error: string | null
  } | null>(null)

  useEffect(() => {
    if (isPreview) return
    const currentWorkspaceId = activeWorkspaceId || null
    let latestSaved: SavedTemplateRecord[] = []
    let latestClones: WorkspaceTemplateClone[] = []
    let latestError: string | null = null

    const stopSaved = subscribeSavedTemplates(
      currentWorkspaceId,
      (templates) => {
        latestSaved = templates
        setSubscribedSaved({
          workspaceId: currentWorkspaceId,
          saved: latestSaved,
          clones: latestClones,
          error: latestError,
        })
      },
      (nextError) => {
        latestError = nextError.message
        setSubscribedSaved({
          workspaceId: currentWorkspaceId,
          saved: latestSaved,
          clones: latestClones,
          error: latestError,
        })
      },
    )

    const stopClones = subscribeWorkspaceTemplateClones(
      currentWorkspaceId,
      (clones) => {
        latestClones = clones
        setSubscribedSaved({
          workspaceId: currentWorkspaceId,
          saved: latestSaved,
          clones: latestClones,
          error: latestError,
        })
      },
      (nextError) => {
        latestError = nextError.message
        setSubscribedSaved({
          workspaceId: currentWorkspaceId,
          saved: latestSaved,
          clones: latestClones,
          error: latestError,
        })
      },
    )

    return () => {
      stopSaved()
      stopClones()
    }
  }, [activeWorkspaceId, isPreview])

  const registry = useMemo(() => {
    if (isPreview) return mergeTemplateRegistryEntries(BUNDLED_EXPLORE_TEMPLATES, category)
    if (subscribedRegistry?.category === category) return subscribedRegistry.templates
    return []
  }, [category, isPreview, subscribedRegistry])

  const loadingRegistry = !isPreview && subscribedRegistry?.category !== category

  const previewTemplates = useMemo(() => {
    if (!isPreview) return []
    return category ? DEBUG_TEMPLATE_CLONES.filter((template) => template.category === category) : DEBUG_TEMPLATE_CLONES
  }, [category, isPreview])

  const savedTemplates = useMemo(() => {
    if (isPreview) return previewTemplates
    if (subscribedSaved?.workspaceId === (activeWorkspaceId || null)) return subscribedSaved.saved
    return []
  }, [activeWorkspaceId, isPreview, previewTemplates, subscribedSaved])

  const localTemplates = useMemo(() => {
    if (isPreview) return previewTemplates
    if (subscribedSaved?.workspaceId === (activeWorkspaceId || null)) return subscribedSaved.clones
    return []
  }, [activeWorkspaceId, isPreview, previewTemplates, subscribedSaved])

  const loadingSaved = Boolean(activeWorkspaceId && !isPreview && subscribedSaved?.workspaceId !== (activeWorkspaceId || null))

  const error = isPreview
    ? null
    : (subscribedRegistry?.category === category ? subscribedRegistry.error : null)
      ?? (subscribedSaved?.workspaceId === (activeWorkspaceId || null) ? subscribedSaved.error : null)

  const savedById = useMemo(
    () => new Map(savedTemplates.map((template) => [template.templateId, template])),
    [savedTemplates],
  )
  const localById = useMemo(
    () => new Map(localTemplates.map((template) => [template.templateId, template])),
    [localTemplates],
  )

  const importTemplateById = useCallback(async (templateId: string) => {
    await importTemplate(activeWorkspaceId, templateId, {
      id: actor?.uid ?? '',
      name: actor?.displayName ?? actor?.email ?? 'Workspace member',
    }, plan, undefined, activeWorkspace)
  }, [activeWorkspace, activeWorkspaceId, actor?.displayName, actor?.email, actor?.uid, plan])

  const deleteTemplateById = useCallback(async (templateId: string) => {
    await deleteLocalTemplate(activeWorkspaceId, templateId, {
      id: actor?.uid ?? '',
      name: actor?.displayName ?? actor?.email ?? 'Workspace member',
    })
  }, [activeWorkspaceId, actor?.displayName, actor?.email, actor?.uid])

  const copyChecklistTemplateToVisionboardById = useCallback(async (templateId: string) => {
    return copyChecklistTemplateToVisionboard(activeWorkspaceId, templateId, {
      id: actor?.uid ?? '',
      name: actor?.displayName ?? actor?.email ?? 'Workspace member',
    }, plan, undefined, activeWorkspace)
  }, [activeWorkspace, activeWorkspaceId, actor?.displayName, actor?.email, actor?.uid, plan])

  const checkIfSaved = useCallback((templateId: string) => checkSavedList(savedTemplates, templateId), [savedTemplates])
  const getSavedTemplatesByCategory = useCallback(
    (nextCategory: TemplateCategory) => localTemplates.filter((template) => template.category === nextCategory),
    [localTemplates],
  )
  const hasExactLocalCopy = useCallback((template: TemplateRegistryEntry) => {
    const local = localById.get(template.id)
    return Boolean(savedById.has(template.id) && local && local.registryVersion === template.registryVersion && !local.isModified)
  }, [localById, savedById])

  return {
    registry,
    savedTemplates,
    localTemplates,
    savedById,
    localById,
    loading: loadingRegistry || loadingSaved,
    error,
    importTemplate: importTemplateById,
    copyChecklistTemplateToVisionboard: copyChecklistTemplateToVisionboardById,
    deleteLocalTemplate: deleteTemplateById,
    checkIfSaved,
    getSavedTemplatesByCategory,
    hasExactLocalCopy,
  }
}
